/**
 * Renders a single challenge history card (picker + MatchResultCard) for use in a virtualized list.
 */

import { View } from 'react-native'
import type { ChallengeWithStats } from '../../../types/challenges'
import type { ChallengeWithParticipantAvatars } from '../../../types/challenges'
import type { GroupLeaderboardEntry } from '../../../src/friends/helpers/groupLeaderboard.helper'
import { groupChallengeToPickHistoryEntry } from '../../../src/friends/helpers/groupChallengeToPickHistoryEntry.helper'
import { ChallengeParticipantSwitcher } from '../../challenges/ChallengeParticipantSwitcher'
import { MatchResultCard } from '../friendStats/MatchResultCard'
import { buildAcceptedOtherParticipants } from './GroupChallengeHistorySection'
import type { StyleProp, ViewStyle } from 'react-native'
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types'

export interface GroupChallengeHistoryCardProps {
  challenge: ChallengeWithStats
  selectedId: string | null
  currentUserId: string
  leaderboardEntries: GroupLeaderboardEntry[]
  memberNames: Record<string, string>
  userAvatarId?: string | null
  /** Current user's custom layered look; overrides userAvatarId on the user side of the card. */
  userAvatarConfig?: EquippedMap | null
  acceptedSet: Set<string>
  onSelectParticipant: (challengeId: string, userId: string) => void
  cardWrapStyle: StyleProp<ViewStyle>
  testID?: string
}

export function GroupChallengeHistoryCard({
  challenge,
  selectedId,
  currentUserId,
  leaderboardEntries,
  memberNames,
  userAvatarId,
  userAvatarConfig = null,
  acceptedSet,
  onSelectParticipant,
  cardWrapStyle,
  testID,
}: GroupChallengeHistoryCardProps) {
  const acceptedOtherParticipants = buildAcceptedOtherParticipants(
    acceptedSet,
    currentUserId,
    leaderboardEntries,
    memberNames
  )
  const entry = groupChallengeToPickHistoryEntry(challenge, currentUserId, selectedId, {
    logMissingMatch: false,
  })
  const friendName = selectedId ? (memberNames[selectedId] ?? 'Unknown') : ''
  const selectedEntry = selectedId
    ? leaderboardEntries.find((e) => e.userId === selectedId)
    : undefined
  const challengeWithAvatars = challenge as ChallengeWithParticipantAvatars
  const friendAvatarId =
    (selectedId && challengeWithAvatars.participantAvatarIdsById?.[selectedId]) ??
    selectedEntry?.avatarId ??
    undefined
  const friendAvatarConfig =
    (selectedId ? challengeWithAvatars.participantAvatarConfigsById?.[selectedId] : null) ??
    selectedEntry?.avatarConfig ??
    null
  const friendAvatarUrl =
    (selectedId && challengeWithAvatars.participantAvatarsById?.[selectedId]) ||
    selectedEntry?.avatar
  const friendFallbackUrl =
    friendAvatarUrl && String(friendAvatarUrl).startsWith('http') ? friendAvatarUrl : undefined

  const creatorName = challenge.creatorId
    ? challenge.creatorId === currentUserId
      ? 'You'
      : (memberNames[challenge.creatorId] ?? null)
    : null;

  const picker =
    acceptedOtherParticipants.length > 0 ? (
      <ChallengeParticipantSwitcher
        participants={acceptedOtherParticipants}
        selectedOpponentId={selectedId}
        onSelectParticipant={(userId) => onSelectParticipant(challenge.id, userId)}
        testID={testID ? `${testID}-switcher-${challenge.id}` : undefined}
      />
    ) : null

  return (
    <View
      style={cardWrapStyle}
      testID={testID ? `${testID}-card-${challenge.id}` : undefined}
    >
      {entry ? (
        <MatchResultCard
          data={entry}
          friendName={friendName}
          userAvatarId={userAvatarId}
          userAvatarConfig={userAvatarConfig}
          friendAvatarId={friendAvatarId ?? null}
          friendAvatarConfig={friendAvatarConfig}
          friendFallbackUrl={friendFallbackUrl}
          headerContent={picker}
          creatorName={creatorName}
          testID={testID ? `${testID}-match-card-${challenge.id}` : undefined}
        />
      ) : (
        picker
      )}
    </View>
  )
}
